import React from "react";
import {
  Users, Handshake, Building2, Home, Briefcase, Store, Landmark,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { Eyebrow, BookButton } from "@/components/Bits";
import { IMAGES } from "@/lib/site";

const localImpact = [
  { icon: Briefcase, t: "Local Jobs" },
  { icon: Store, t: "Main Streets & Services" },
  { icon: Landmark, t: "Community Tax Base" },
  { icon: Users, t: "Community Continuity" },
];

const pathways = [
  { icon: Handshake, t: "Third-Party Sale", b: "Private equity, strategic buyer, ETA / search fund, or a local buyer." },
  { icon: Users, t: "Internal Sale / MBO", b: "A management buyout led by the existing leadership team." },
  { icon: Building2, t: "Employee Ownership Trust", b: "Transitioning ownership to employees through an EOT structure." },
  { icon: Home, t: "Family or Chosen Successor", b: "A transition to family, a friend, or a chosen successor." },
];

const readinessReqs = [
  "Financial clarity", "Operational documentation", "Reduced owner dependency", "Successor capability",
  "Financing", "Governance", "Tax planning", "Legal structure", "Employee continuity", "Customer continuity",
];

const WorkshopsPage = () => {
  return (
    <main data-testid="workshops-page" className="pt-20">
      {/* HERO */}
      <section className="bg-cream py-20 md:py-28 relative">
        <div className="grid-lines" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Eyebrow>Workshops · For Economic Development Partners</Eyebrow>
              <h1 className="font-serif text-4xl md:text-[3.4rem] leading-[1.1] text-ink-deep">
                Succession Readiness <span className="italic text-brand">Workshops.</span>
              </h1>
              <p className="mt-7 text-lg text-[#475569]">
                A practical pilot for owner-led businesses preparing for sale, succession, or transition —
                delivered in person, in your community.
              </p>
              <p className="mt-4 text-base text-[#64748B] max-w-2xl">
                Cultural Capital is seeking a select few economic development partners across Ontario to host
                in-person succession-readiness workshops. We facilitate; you convene your community.
              </p>
              <div className="mt-9">
                <BookButton label="Host a Pilot Workshop" variant="dark" testId="workshops-hero-cta" />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden border border-[#E2E8F0] bg-[#EEF2F6]">
                <img src={IMAGES.building1} alt="Community and main street" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY THIS MATTERS LOCALLY */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Why This Matters Locally</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep leading-tight">
              Succession is not just a private retirement issue.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <p className="text-lg text-[#475569] mb-8">
              Business succession affects local jobs, services, main streets, the tax base, and community
              continuity. That impact is even more evident in smaller communities — where a single owner-led
              business can anchor an entire main street.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {localImpact.map((c, i) => (
                <Reveal key={i} delay={i * 70} className="flex items-center gap-4 bg-cream border border-[#E2E8F0] p-5">
                  <span className="w-11 h-11 flex items-center justify-center bg-white border border-[#E2E8F0]">
                    <c.icon className="w-5 h-5 text-brand" />
                  </span>
                  <p className="font-serif text-lg text-ink-deep">{c.t}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE READINESS GAP */}
      <section className="bg-ink-deep py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-10 text-center">
          <Eyebrow light className="mx-auto w-fit">The Readiness Gap</Eyebrow>
          <Reveal>
            <p className="font-serif text-2xl md:text-3xl text-white leading-snug">
              Owners are told to speak with a lawyer, accountant, broker, or valuator — but many are not ready
              for that step yet. First, they need to understand{" "}
              <span className="text-brand-light">which transition pathways are realistic</span>, what each path
              requires, and what may be making the business harder to transfer.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE PROPOSED WORKSHOP */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Eyebrow>The Proposed Workshop</Eyebrow>
              <h2 className="font-serif text-3xl md:text-4xl text-ink-deep mb-5">
                “Is Your Business Ready to Transition?”
              </h2>
              <p className="text-lg text-[#475569] mb-4">
                A practical session for local business owners considering sale, succession, internal transition, or
                long-term continuity.
              </p>
              <p className="text-[#64748B]">
                The workshop helps owners understand their options, identify common readiness gaps, and prepare for
                more effective conversations with professional advisors.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="bg-ink-deep p-9 text-center">
                <Users className="w-8 h-8 text-brand-light mx-auto mb-4" />
                <span className="font-serif text-5xl text-white block">50</span>
                <p className="text-[#94A3B8] mt-3">Target attendance — local business owners &amp; succession stakeholders.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRANSITION PATHWAYS */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <Eyebrow>Workshop Content</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">The main transition pathways.</h2>
            <p className="mt-4 text-[#475569]">
              Most owner-led businesses will need to consider one or more of the following routes.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pathways.map((p, i) => (
              <Reveal key={i} delay={i * 90} className="border border-[#E2E8F0] bg-cream p-7 hover:border-brand transition-colors">
                <p.icon className="w-8 h-8 text-brand mb-5" />
                <h3 className="font-serif text-xl text-ink-deep mb-2">{p.t}</h3>
                <p className="text-sm text-[#475569]">{p.b}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12 bg-ink-deep p-9 md:p-12">
            <h3 className="font-serif text-2xl text-white mb-3">Different paths require different preparation.</h3>
            <p className="text-[#94A3B8] mb-7 max-w-3xl">
              Each pathway carries its own readiness requirements. The workshop maps these clearly so owners know
              where they stand.
            </p>
            <div className="flex flex-wrap gap-3">
              {readinessReqs.map((r, i) => (
                <span key={i} className="text-sm text-[#CBD5E1] border border-[#334155] px-4 py-2">{r}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* LOCAL RESOURCE PARTNER INTEGRATION */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Resource Partner Integration</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">Built around your local advisor ecosystem.</h2>
          </div>
          <div className="lg:col-span-8 space-y-5">
            <p className="text-lg text-[#475569]">
              Local accountants, lawyers, lenders, brokers, financial planners, chambers, SBECs, and other advisors
              may be invited to participate as resource partners.
            </p>
            <p className="text-[#475569]">
              Resource partners can contribute printed materials, checklists, contact cards, or practical
              “questions to ask” resources for attendees.
            </p>
            <blockquote className="border-l-2 border-brand pl-6 py-1 font-serif text-xl md:text-2xl text-ink-deep italic">
              CCA facilitates the workshop and keeps the session practical, neutral, and education-first.
            </blockquote>
          </div>
        </div>
      </section>

      {/* THE ASK / CTA */}
      <section className="bg-ink-deep py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-5xl text-white leading-tight">
            Would your office be open to hosting a pilot succession-readiness workshop?
          </h2>
          <p className="mt-6 text-lg text-[#94A3B8]">
            We’re seeking local economic development partners interested in hosting an in-person pilot workshop for
            owner-led businesses in their community.
          </p>
          <div className="mt-9 flex justify-center">
            <BookButton label="Book a Partnership Call" variant="light" testId="workshops-final-cta" />
          </div>
          <p className="mt-6 text-sm text-[#64748B]">
            Preferred format: in-person. Virtual or hybrid delivery can be considered where geography,
            accessibility, or partner needs make it useful.
          </p>
        </div>
      </section>
    </main>
  );
};

export default WorkshopsPage;
